import { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import "./SurveyAssignModal.scss";
import FormModal from "../../../components/FormModal/FormModal";
import Buttons from "../../../components/Buttons/Buttons";
import SelectInput from "../../../components/SelectInput/SelectInput";
import FieldLabel from "../../../components/FieldLabel/FieldLabel";
import {
  GetSurveyList,
  AssignSurveyToPatient,
  GetSurveyAssignments,
} from "../../../utils/apiCalls/surveyBuilder";
import { GetAllPatientDetailsHealthProfessional } from "../../../utils/apiCalls/patients";
import { isAssignable } from "../../../utils/surveysConstants";
import { useToast } from "../../../utils/ToastContext";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Assigned/Completed patients already have this survey; Revoked ones are
// fair game to assign again, so they aren't excluded from the picker.
// Matched on statusName, not the numeric status code — GetSurveyAssignments'
// codes on the live backend don't line up with the documented 1/2/3
// (a "Completed" row came back with status: 3, not 2), so statusName is the
// only reliable signal, same reasoning as normalizeStatus above.
const ACTIVE_ASSIGNMENT_STATUS_NAMES = ["assigned", "completed"];

// Assign a published survey to one or more patients. Either `survey` or `patientId` may
// be pre-supplied by the caller (survey list vs. patient detail entry
// points) — whichever one isn't, gets a picker.
const SurveyAssignModal = ({ survey, patientId, patientName, onClose, onAssigned }) => {
  const { showToast } = useToast();
  const [surveyOptions, setSurveyOptions] = useState([]);
  const [patientOptions, setPatientOptions] = useState([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState(survey?.id || "");
  const [selectedPatientIds, setSelectedPatientIds] = useState(patientId ? [patientId] : []);
  const [assignedPatients, setAssignedPatients] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(!survey || !patientId);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let ignore = false;
    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        if (!survey) {
          const res = await GetSurveyList({ pageIndex: 0, pageSize: 100, searchString: "" });
          if (!ignore && res.statusCode === 200) {
            setSurveyOptions(
              res.data.reduce((acc, s) => {
                if (isAssignable(s)) acc.push({ value: s.id, label: s.title });
                return acc;
              }, []),
            );
          }
        }
        if (!patientId) {
          const res = await GetAllPatientDetailsHealthProfessional({ pageIndex: 0, pageSize: 200, searchString: "" });
          if (!ignore && res.statusCode === 200) {
            setPatientOptions((res.data || []).map((p) => ({ value: p.id, label: p.name })));
          }
        }
        // Show who already has this survey so the picker doesn't invite a
        // duplicate assignment — only meaningful once a survey is fixed
        // (the survey-list "Assign" entry point), not the patient-detail one.
        if (survey) {
          const res = await GetSurveyAssignments({ surveyId: survey.id, pageIndex: 0, pageSize: 200, status: 0 });
          if (!ignore && res.statusCode === 200) {
            setAssignedPatients(res.data);
          }
        }
      } finally {
        if (!ignore) setLoadingOptions(false);
      }
    };
    loadOptions();
    return () => {
      ignore = true;
    };
  }, [survey, patientId]);

  const activelyAssignedIds = new Set(
    assignedPatients.reduce((acc, a) => {
      if (ACTIVE_ASSIGNMENT_STATUS_NAMES.includes((a.statusName || "").toLowerCase())) {
        acc.push(a.patientId);
      }
      return acc;
    }, []),
  );
  const selectablePatientOptions = patientOptions.filter((p) => !activelyAssignedIds.has(p.value));
  const selectedPatientIdSet = new Set(selectedPatientIds);

  const handleAssign = async () => {
    if (!selectedSurveyId || selectedPatientIds.length === 0) {
      showToast("Please select both a survey and at least one patient", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await AssignSurveyToPatient({ surveyId: selectedSurveyId, patientIds: selectedPatientIds });
      if (res.statusCode === 200) {
        showToast(`Survey assigned to ${selectedPatientIds.length} patient(s)`, "success");
        onAssigned?.();
        onClose();
      } else {
        showToast(res.message || "Failed to assign survey", "error");
      }
    } catch (error) {
      showToast("An error occurred during assignment", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormModal
      open
      onClose={onClose}
      title="Assign Survey"
      actions={
        <>
          <Buttons text="Cancel" type="button" variant="secondary" onClick={onClose} style={{ width: "100px" }} />
          <Buttons
            text={isSubmitting ? "Assigning..." : "Assign"}
            type="button"
            variant="primary"
            onClick={handleAssign}
            disabled={isSubmitting}
            style={{ width: "120px" }}
          />
        </>
      }
    >
      {loadingOptions ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <CircularProgress />
        </div>
      ) : (
        <div className="survey-assign-modal">
          {survey ? (
            <div className="survey-assign-modal_survey-info">
              <span className="survey-assign-modal_survey-label">Survey</span>
              <span className="survey-assign-modal_survey-title">{survey.title}</span>
            </div>
          ) : (
            <SelectInput
              title="Survey*"
              name="surveyId"
              placeholder="Select a published survey"
              value={selectedSurveyId}
              onChange={(e) => setSelectedSurveyId(e.target.value)}
              options={surveyOptions}
            />
          )}

          {patientId ? (
            <div>
              <strong>Patient:</strong> {patientName}
            </div>
          ) : (
            <div>
              <FieldLabel htmlFor="patient-select">Patient(s)*</FieldLabel>

              {assignedPatients.length > 0 && (
                <>
                  <div className="survey-assign-modal_group-label">Already assigned</div>
                  <div className="survey-assign-modal_chip-row">
                    {assignedPatients.map((a) => (
                      <Chip
                        key={a.id}
                        size="small"
                        label={`${a.patientName} · ${a.statusName}`}
                        variant="outlined"
                      />
                    ))}
                  </div>
                </>
              )}

              {selectedPatientIds.length > 0 && (
                <>
                  <div className="survey-assign-modal_group-label">Selected to assign</div>
                  <div className="survey-assign-modal_chip-row">
                    {patientOptions.reduce((acc, p) => {
                      if (!selectedPatientIdSet.has(p.value)) return acc;
                      acc.push(
                        <Chip
                          key={p.value}
                          size="small"
                          color="primary"
                          label={p.label}
                          onDelete={() => setSelectedPatientIds((prev) => prev.filter((id) => id !== p.value))}
                        />,
                      );
                      return acc;
                    }, [])}
                  </div>
                </>
              )}

              <Autocomplete
                multiple
                id="patient-select"
                className="survey-assign-modal_search"
                options={selectablePatientOptions}
                disableCloseOnSelect
                getOptionLabel={(option) => option.label}
                value={patientOptions.filter((p) => selectedPatientIdSet.has(p.value))}
                onChange={(event, newValue) => {
                  setSelectedPatientIds(newValue.map((val) => val.value));
                }}
                renderTags={() => null}
                ListboxProps={{
                  style: {
                    maxHeight: "200px",
                  },
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.label}
                  </li>
                )}
                sx={{ width: "100%" }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Search and select patients..." size="small" />
                )}
              />
            </div>
          )}
        </div>
      )}
    </FormModal>
  );
};

export default SurveyAssignModal;
